import { useCallback, useEffect, useMemo } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { TutorialKeys } from '@/_shared/constants';
import { getEnvironment, isMockRunning } from '@/_shared/helpers';
import { getUserProfileCall } from '@/core/state/api';
import { cleanLoggedUser } from '../actions';
import { getAuthSettingsCall } from '../api';
import { getLoggedUser } from '../selectors';

export const useLogin = () => {
  const dispatch = useDispatch();
  const ldClient = useLDClient();
  const user = useSelector(getLoggedUser);
  const { isLocalhost } = getEnvironment();
  const { logoutUrl } = getAuthSettingsCall.hook();
  const { response: responseGetUserProfile, currentUserLanguage } = getUserProfileCall.hook();

  const resetSession = useCallback(() => {
    dispatch(cleanLoggedUser());
    // We'll keep in an array the enum TutorialKeys's values, in order to keep only the tutorial keys in localStorage and not reset the seen tutorial status on logout
    const tutorialKeys = Object.values(TutorialKeys).map(key => String(key));

    const seenTutorials: Record<string, string> = {};
    Object.keys(localStorage).forEach(storageKey => {
      // We want to keep only the tutorial keys, in order to not reset the seen tutorial status on logout
      if (storageKey && tutorialKeys.some(tutorialKey => storageKey?.includes(tutorialKey))) {
        seenTutorials[storageKey] = localStorage.getItem(storageKey) || '';
      }
    });

    localStorage.clear();
    sessionStorage.clear();

    Object.entries(seenTutorials).forEach(([key, value]) => {
      if (value) {
        localStorage.setItem(key, value);
      }
    });

    if (currentUserLanguage) {
      // TODO: use it when the language will save on database
      // localStorage.setItem('lang', currentUserLanguage);
    }
  }, [currentUserLanguage, dispatch]);

  const logout = useCallback(() => {
    resetSession();
    window.location.href = `${logoutUrl}?post_logout_redirect_uri=${window.location.origin}?logout=true&id_token_hint=${user?.id_token}`;
  }, [logoutUrl, resetSession, user?.id_token]);

  const isUserLogged = useMemo(() => {
    const hasToken = !!sessionStorage.getItem('accessToken');
    const checkUser = isMockRunning() ? hasToken : hasToken && user;

    if (checkUser) {
      return true;
    }
    return false;
  }, [user]);

  // set user data in LaunchDarkly
  useEffect(() => {
    if (ldClient && responseGetUserProfile?.email) {
      ldClient.identify({
        key: 'user-key',
        name: `${responseGetUserProfile.firstName} ${responseGetUserProfile.lastName}`,
        email: responseGetUserProfile?.email,
        custom: {},
      });
    }
  }, [ldClient, responseGetUserProfile]);

  // set user data in Datadog
  useEffect(() => {
    if (datadogRum && responseGetUserProfile?.email && !isLocalhost) {
      datadogRum.setUser({
        name: `${responseGetUserProfile?.firstName} ${responseGetUserProfile?.lastName}`,
        email: responseGetUserProfile?.email,
      });
    }
  }, [isLocalhost, responseGetUserProfile]);

  return {
    logout,
    resetSession,
    isUserLogged,
  };
};
